import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect } from 'react';
import SHA256 from 'crypto-js/sha256';
import encHex from 'crypto-js/enc-hex';

export const useObjectCache = () => {
    const dispatch = useDispatch();
    const caches = useSelector(state => state.objectCache.caches);

    // Load cache from localStorage on mount
    useEffect(() => {
        try {
            const savedCache = localStorage.getItem('objectCache');
            if (savedCache) {
                const parsedCache = JSON.parse(savedCache);
                dispatch({ type: 'HYDRATE_OBJECT_CACHE', payload: parsedCache });
            }
        } catch (error) {
            console.warn('Failed to load object cache from localStorage:', error);
        }
    }, [dispatch]);

    // Save cache to localStorage whenever caches change
    useEffect(() => {
        try {
            localStorage.setItem('objectCache', JSON.stringify({ caches }));
        } catch (error) {
            console.warn('Failed to save object cache to localStorage:', error);
        }
    }, [caches]);

    // Save an object to cache
    const saveToCache = useCallback((schemaKey, objectKey, value) => {
        dispatch({
            type: 'SAVE_TO_CACHE',
            payload: { schemaKey, objectKey, value }
        });
    }, [dispatch]);

    // Get cached object
    const getFromCache = useCallback((schemaKey, objectKey) => {
        return caches[schemaKey]?.[objectKey];
    }, [caches]);

    // Get all cached objects for a schema
    const getCachedObjects = useCallback((schemaKey) => {
        return caches[schemaKey] || {};
    }, [caches]);

    // Delete from cache
    const deleteFromCache = useCallback((schemaKey, objectKey) => {
        dispatch({
            type: 'DELETE_FROM_CACHE',
            payload: { schemaKey, objectKey }
        });
    }, [dispatch]);

    // Clear all caches for a schema
    const clearSchemaCache = useCallback((schemaKey) => {
        dispatch({
            type: 'CLEAR_SCHEMA_CACHE',
            payload: { schemaKey }
        });
    }, [dispatch]);

    // Generate a unique key for a schema based on its structure
    const getSchemaKey = useCallback((schema) => {
        if (!schema) return null;
        
        // Create a hash-like key based on schema structure
        const schemaString = JSON.stringify(schema, Object.keys(schema).sort());
        return `schema_${schemaString.length}_${schemaString.slice(0, 50)}`;
    }, []);

    // Canonical, deep, stable JSON stringify (keys sorted at all levels)
    const stableStringify = useCallback((value) => {
        if (value === null || typeof value !== 'object') {
            return JSON.stringify(value);
        }
        if (Array.isArray(value)) {
            return '[' + value.map((item) => stableStringify(item)).join(',') + ']';
        }
        const keys = Object.keys(value).sort();
        const entries = keys.map((k) => JSON.stringify(k) + ':' + stableStringify(value[k]));
        return '{' + entries.join(',') + '}';
    }, []);

    // Generate a strong hash key for an object based on its canonical content
    const generateObjectHash = useCallback((obj) => {
        if (!obj || typeof obj !== 'object') return null;
        const canonical = stableStringify(obj);
        // Use SHA-256 and keep first 24 hex chars (96 bits) to minimize collisions
        const digest = SHA256(canonical).toString(encHex);
        return digest.slice(0, 24);
    }, [stableStringify]);

    // Auto-cache an object (saves automatically without user input)
    const autoCacheObject = useCallback((schemaKey, obj) => {
        if (!obj || typeof obj !== 'object' || Object.keys(obj).length === 0) return null;
        
        const hashKey = generateObjectHash(obj);
        if (!hashKey) return null;
        
        // Only cache if it's not already cached
        if (!caches[schemaKey]?.[hashKey]) {
            dispatch({
                type: 'SAVE_TO_CACHE',
                payload: { schemaKey, objectKey: hashKey, value: obj }
            });
        }
        
        return hashKey;
    }, [dispatch, caches, generateObjectHash]);

    // Deep clone an object
    const deepClone = useCallback((obj) => {
        return JSON.parse(JSON.stringify(obj));
    }, []);

    // Export cache as JSON
    const exportAsJSON = useCallback(() => {
        return JSON.stringify({ caches }, null, 2);
    }, [caches]);

    // Import cache from JSON
    const importFromJSON = useCallback((jsonString) => {
        try {
            const parsed = JSON.parse(jsonString);
            dispatch({ type: 'HYDRATE_OBJECT_CACHE', payload: parsed });
            return true;
        } catch (error) {
            console.error('Failed to import cache from JSON:', error);
            return false;
        }
    }, [dispatch]);

    // Clear all caches
    const clearAllCaches = useCallback(() => {
        dispatch({ type: 'HYDRATE_OBJECT_CACHE', payload: { caches: {} } });
    }, [dispatch]);

    return {
        saveToCache,
        getFromCache,
        getCachedObjects,
        deleteFromCache,
        clearSchemaCache,
        getSchemaKey,
        generateObjectHash,
        autoCacheObject,
        deepClone,
        exportAsJSON,
        importFromJSON,
        clearAllCaches
    };
};
